import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Delete, ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import twilioService, { TwilioApiKey } from '../../services/twilioService';
import { useOrganization } from '../../pages/Organization/OrganizationContext';

const TwilioApiKeysComponent: React.FC = () => {
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<TwilioApiKey[]>([]);
  const [friendlyName, setFriendlyName] = useState('');
  const [newKey, setNewKey] = useState<TwilioApiKey | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  useEffect(() => {
    if (organization?._id) {
      fetchApiKeys();
    }
  }, [organization]);

  const fetchApiKeys = async () => {
    if (!organization?._id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await twilioService.listApiKeys(organization._id);
      setApiKeys(data);
    } catch (err) {
      setError('Failed to fetch API keys');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!organization?._id || !friendlyName) return;

    setCreating(true);
    setError(null);

    try {
      const data = await twilioService.createApiKey(organization._id, friendlyName);
      setNewKey(data.apiKey);
      setOpenDialog(true);
      setFriendlyName('');
      // Refresh the list after creating
      fetchApiKeys();
    } catch (err) {
      setError('Failed to create API key');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const deleteApiKey = async (keySid: string) => {
    if (!organization?._id) return;

    try {
      await twilioService.deleteApiKey(organization._id, keySid);
      // Refresh the list after deletion
      fetchApiKeys();
    } catch (err) {
      setError('Failed to delete API key');
      console.error(err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewKey(null);
    setShowSecret(false);
  };

  const columns: GridColDef[] = [
    { field: 'friendlyName', headerName: 'Friendly Name', flex: 1 },
    { field: 'sid', headerName: 'SID', flex: 1 },
    { field: 'secretPreview', headerName: 'Secret', flex: 1 },
    {
      field: 'dateCreated',
      headerName: 'Created',
      flex: 1,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <div title="Delete">
          <button
            onClick={() => deleteApiKey(params.row.sid)}
            className="p-1 text-red-600 hover:text-red-800 rounded-full hover:bg-red-100"
          >
            <Delete fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold">Twilio API Keys</h2>
      </div>
      <hr className="border-gray-200" />
      <div className="p-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}

        <div className="mb-6">
          <p className="mb-4">
            Create a new API key for your Twilio subaccount:
          </p>

          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Friendly Name
              </label>
              <input
                type="text"
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={createApiKey}
              disabled={creating || !friendlyName}
              className={`flex items-center px-4 py-2 rounded-md text-white font-medium ${creating || !friendlyName
                ? 'bg-blue-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>Creating...</span>
                </>
              ) : (
                'Create API Key'
              )}
            </button>
          </div>
        </div>

        <hr className="my-4 border-gray-200" />

        <h3 className="text-lg font-semibold mb-2">Your API Keys</h3>

        {loading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="h-[400px] w-full">
            <DataGrid
              rows={apiKeys.map(key => ({ ...key, id: key.sid }))}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20]}
              className="border-0 shadow-none"
            />
          </div>
        )}
      </div>

      {/* Dialog for displaying new API key */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">API Key Created Successfully</h3>
            </div>
            <div className="p-6">
              <div className="mb-4 px-4 py-3 bg-yellow-100 text-yellow-800 rounded-md" role="alert">
                This is the only time the API key secret will be displayed. Please save it securely.
              </div>

              {newKey && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Friendly Name:</p>
                    <div className="flex items-center gap-2 mb-2">
                      <p>{newKey.friendlyName}</p>
                      <button
                        onClick={() => copyToClipboard(newKey.friendlyName)}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                      >
                        <ContentCopy fontSize="small" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-600">SID:</p>
                    <div className="flex items-center gap-2 mb-2">
                      <p>{newKey.sid}</p>
                      <button
                        onClick={() => copyToClipboard(newKey.sid)}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                      >
                        <ContentCopy fontSize="small" />
                      </button>
                    </div>
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <p className="text-sm font-medium text-gray-600">Secret:</p>
                    <div className="flex items-center gap-2">
                      <p>
                        {showSecret ? newKey.secret : '••••••••••••••••••••••••••'}
                      </p>
                      <button
                        onClick={() => setShowSecret(!showSecret)}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                      >
                        {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(newKey.secret)}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                      >
                        <ContentCopy fontSize="small" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-3 bg-gray-50 flex justify-end border-t border-gray-200">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwilioApiKeysComponent; 